import Head from 'next/head';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import { useTranslations } from 'ilenia';

import {
  TwitterIcon,
  FacebookIcon,
  LinkedInIcon,
} from '@bscom/components/Icons';
import { Link } from '@bscom/components/Links';
import { trackButtonClick } from '@bscom/tracking';
import { H1, H3 } from '@bscom/components/Headers';
import { Author } from '@bscom/components/Author';
import { tablets } from '@bscom/styling/breakpoints';
import { Spacing } from '@bscom/components/Spacing';
import { Markdown } from '@bscom/components/Markdown';
import {
  MAX_PAGE_WIDTH,
  STANDARD_ARTICLE_WIDTH,
} from '@bscom/constants/widths';
import { getLocaleByCode } from '@bscom/locales';
import { HeadMetaTags } from '@bscom/components/HeadMetaTags';
import { renderContentfulEntry } from '@bscom/utilities/renderers';
import { oldBrandComponents } from '@bscom/renderers/old-brand-renderer';
import { RichText } from '@bscom/components/RichText';

import { formatDate } from 'lib/date';
import { useResource } from 'main-hooks';
import { pillarLabels } from 'constants/resources';
import { renderContentBlock } from 'lib/renderers';
import { filteredEnglishLocales } from 'lib/locales';
import { ResourceList } from 'components/ResourceList';
import { GatedContent } from 'components/GatedContent';
import { ResourceSeoHead } from 'components/ResourceSeoHeads';

const TopPillarLink = styled(Link)`
  display: block;
  text-align: center;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.colors.blueSaturated};
`;

const Title = styled(H1)`
  ${({ theme }) => `
    text-align: center;
    margin-bottom: 24px;
    font-size: ${theme.fontSizes['2xl']};
    line-height: ${theme.leadings.xl};

    @media (${tablets}) {
      font-size: ${theme.fontSizes['4xl']};
      line-height: ${theme.leadings['3xl']};
    }
  `}
`;

const PublishedDate = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.greyDark};
`;

const Introduction = styled(Markdown)`
  ${({ theme }) => `
    padding: 0 15px;
    margin: 0 auto 60px auto;
    font-size: ${theme.fontSizes.lg};
    line-height: ${theme.leadings.lg};
    max-width: ${theme.widths.standardArticle}px;

    @media (${tablets}) {
      padding: 0 30px;
      font-size: ${theme.fontSizes.xl};
      line-height: ${theme.leadings.xl};
    }
  `}
`;

const ArticleContent = styled.div`
  content-visibility: auto;
  contain-intrinsic-size: 1200px;

  ${Link} {
    color: ${({ theme }) => theme.colors.blueSaturated};
  }
`;

const RelatedResourceHeader = styled(H3)`
  text-align: center;
  margin: 0 0 48px 0;

  @media (${tablets}) {
    margin: 0 0 70px 0;
  }
`;

const ResourceFooter = styled.div`
  margin-bottom: 48px;
  border-width: 1px 0;
  border-style: solid;
  border-color: ${({ theme }) => theme.colors.greyLight};

  @media (${tablets}) {
    margin-bottom: 106px;
  }
`;

const ResourceIcon = styled.img`
  width: 120px;
  display: block;
  margin: 0 auto 30px auto;
`;

const SocialIconsWrapper = styled.div`
  width: 100%;
  padding: 18px 0 24px 0;
  border-width: 1px 0 0 0;
  border-style: solid;
  border-color: ${({ theme }) => theme.colors.greyLight};

  @media (${tablets}) {
    padding: 18px 0 28px 0;
  }
`;

const SectionName = styled.div`
  ${({ theme }) => `
    font-weight: bold;
    margin-bottom: 14px;
    font-size: ${theme.fontSizes.xs};
    line-height: ${theme.fontSizes.lg};
  `}
`;

const SocialIcons = styled.ul`
  padding: 0;
  list-style-type: none;
`;

const SocialShareIconItem = styled.li`
  width: 40px;
  height: 40px;
  cursor: pointer;
  margin-right: 10px;
  align-items: center;
  border-radius: 30px;
  display: inline-flex;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.greyLightest};

  &:last-of-type {
    margin-right: 0;
  }

  @media (${tablets}) {
    width: 50px;
    height: 50px;
  }
`;

const SocialShareIconLink = styled.a`
  width: 100%;
  height: 100%;
  display: inherit;
  align-items: inherit;
  justify-content: inherit;
  border-radius: inherit;

  & > svg {
    width: 16px;
    height: 16px;
    & > path {
      fill: #ffffff;
    }
  }
`;

const FacebookIconLinkWrapper = styled(SocialShareIconLink)`
  background-color: #4267b2;
`;

const TwitterIconLinkWrapper = styled(SocialShareIconLink)`
  background-color: #48aae6;
`;

const LinkedInIconLinkWrapper = styled(SocialShareIconLink)`
  background-color: #0077b5;
`;

const HiddenData = styled.div`
  display: none;
`;

const resourceModules = {
  resourceVideo: dynamic(
    () => import('@bscom/components/Video').then((mod) => mod.ResourceVideo),
    {
      ssr: false,
    }
  ),
  resourceImage: dynamic(() =>
    import('@bscom/content-blocks/ResourceImage').then(
      (mod) => mod.ResourceImageWithSpacing
    )
  ),
};

const renderResourcePageEntry = renderContentfulEntry({
  ...oldBrandComponents,
  ...resourceModules,
});

const ResourceMetaTags = ({ resource, resourceUrl, metaNoindex }) => {
  const [translations] = useTranslations();

  return (
    <HeadMetaTags
      metaTitle={`${
        resource.title
          ? `${resource.title} - ${translations['meta/article/title']}`
          : translations['meta/title']
      }`}
      metaDescription={
        resource.metaDescription ||
        resource.intro ||
        translations['meta/description']
      }
      metaNoindex={metaNoindex || resource.metaNoindex}
      metaOgUrl={resourceUrl ? resourceUrl : null}
      metaOgType="article"
      metaOgImage={
        resource.featuredResource?.image?.url || resource.thumbnail.url
      }
    />
  );
};

export const RelatedResources = ({ relatedResources }) => {
  const [translations] = useTranslations();

  return (
    relatedResources.length > 0 && (
      <>
        <RelatedResourceHeader>
          {translations['reviews/related']}
        </RelatedResourceHeader>
        <Spacing
          withXPadding
          spaces={{ bottom: 75 }}
          contentWidth={MAX_PAGE_WIDTH}
        >
          <ResourceList
            resources={relatedResources.map(
              ({ slug, title, thumbnail, pillar, industry }) => ({
                slug,
                title,
                pillar,
                industry,
                thumbnail,
              })
            )}
          />
        </Spacing>
      </>
    )
  );
};

export const BelowTheFoldContent = ({ resource }) => {
  const { btfContentCollection } = resource;

  return btfContentCollection
    ? btfContentCollection?.items.map(renderContentBlock)
    : '';
};

export const SingleResourceArticle = ({
  pillar,
  industry,
  resource,
  resourceIcon,
}) => {
  const {
    slug,
    date,
    title,
    intro,
    author,
    locale,
    content,
    thumbnail,
    hideFromPages,
    featuredResource,
    sys: { publishedAt, firstPublishedAt },
  } = resource;

  const resourceName = useResource();
  const resourceArea = industry ? industry : pillar;
  const [translations, localeCode] = useTranslations();
  const subdomain = getLocaleByCode(localeCode)?.subdomain;
  const createResourceUrl = (subdomain) =>
    `https://${
      subdomain ? `${subdomain}.` : ''
    }business.trustpilot.com/${resourceName}/${resourceArea}/${slug}`;

  const inItsOwnLocale =
    (filteredEnglishLocales.find(({ code }) => code === localeCode) &&
      locale === 'US') ||
    locale === getLocaleByCode(localeCode).countryCode;

  const richTextModules = {
    ...resourceModules,
    gatedContent: (props) => (
      <GatedContent
        form={props.gatedForm}
        hasFading={props.gatedFormHasFading !== false}
        slug={slug}
        {...props}
      />
    ),
  };

  return (
    <>
      <ResourceMetaTags
        resource={resource}
        resourceUrl={createResourceUrl(subdomain)}
        metaNoindex={!inItsOwnLocale || hideFromPages}
      />
      {inItsOwnLocale && (
        <ResourceSeoHead
          main={resourceName}
          pillar={resourceArea}
          resource={slug}
        />
      )}
      <Head>
        <meta
          itemProp="dateCreated datePublished"
          content={formatDate(firstPublishedAt)}
        />
        <meta name="pubdate" content={formatDate(firstPublishedAt)} />
        {firstPublishedAt !== publishedAt && (
          <meta name="last_updated" content={formatDate(publishedAt)} />
        )}
      </Head>
      <div itemScope itemType="http://schema.org/BlogPosting">
        <meta
          itemScope
          itemProp="mainEntityOfPage"
          itemType="https://schema.org/WebPage"
          itemID={createResourceUrl(subdomain)}
        />
        <Spacing
          withXPadding
          contentWidth={870}
          spaces={{ top: 76, bottom: 40 }}
        >
          {resourceIcon?.url && (
            <ResourceIcon
              src={resourceIcon.url}
              alt={resourceIcon.title}
              title={resourceIcon.description}
            />
          )}
          {pillar && (
            <TopPillarLink href={`/${resourceName}/${pillar}`}>
              {translations[pillarLabels[pillar]]}
            </TopPillarLink>
          )}
          <Title centered itemProp="name headline">
            {title}
          </Title>
          <PublishedDate itemProp="dateCreated datePublished">
            {new Date(date).toLocaleDateString(localeCode, {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </PublishedDate>
          {firstPublishedAt !== publishedAt && (
            <meta itemProp="dateModified" content={formatDate(publishedAt)} />
          )}
        </Spacing>

        <meta
          itemProp="image"
          content={featuredResource?.image?.url || thumbnail.url}
        />

        <ArticleContent>
          {featuredResource && renderResourcePageEntry(featuredResource)}

          {intro && <Introduction>{intro}</Introduction>}

          <Spacing
            withXPadding
            spaces={[{ bottom: 64 }]}
            contentWidth={STANDARD_ARTICLE_WIDTH}
          >
            <RichText content={content} customModules={richTextModules} />
          </Spacing>
          <Spacing withXPadding contentWidth={STANDARD_ARTICLE_WIDTH}>
            <ResourceFooter>
              <Author {...author} />
              <HiddenData
                itemScope
                itemProp="publisher"
                itemType="https://schema.org/Organization"
              >
                <div
                  itemScope
                  itemProp="logo"
                  itemType="https://schema.org/ImageObject"
                >
                  <img
                    aria-label="trustpilot logo"
                    src="https://cdn.trustpilot.net/brand-assets/2.0.0/favicons/favicon-dark-256x256.png"
                  />
                  <meta
                    itemProp="url"
                    content="https://cdn.trustpilot.net/brand-assets/2.0.0/favicons/favicon-dark-256x256.png"
                  />
                </div>
                <meta itemProp="name" content="Trustpilot" />
              </HiddenData>
              <SocialIconsWrapper>
                <SectionName>{translations['social/share']}</SectionName>
                <SocialIcons>
                  <SocialShareIconItem
                    onClick={() =>
                      trackButtonClick({
                        label: 'facebook',
                        location: 'share',
                      })
                    }
                  >
                    <FacebookIconLinkWrapper
                      href={`https://www.facebook.com/sharer/sharer.php?u=${createResourceUrl(
                        subdomain
                      )}`}
                      target="_blank"
                      aria-label="Facebook share button"
                      rel="noopener noreferrer"
                    >
                      <FacebookIcon title="Facebook" />
                    </FacebookIconLinkWrapper>
                  </SocialShareIconItem>
                  <SocialShareIconItem
                    onClick={() =>
                      trackButtonClick({
                        label: 'twitter',
                        location: 'share',
                      })
                    }
                  >
                    <TwitterIconLinkWrapper
                      href={`https://twitter.com/intent/tweet?status=${encodeURIComponent(
                        title
                      )}+${createResourceUrl(subdomain)}`}
                      target="_blank"
                      aria-label="Twitter share button"
                      rel="noopener noreferrer"
                    >
                      <TwitterIcon title="Twitter" />
                    </TwitterIconLinkWrapper>
                  </SocialShareIconItem>
                  <SocialShareIconItem
                    onClick={() =>
                      trackButtonClick({
                        label: 'linkedin',
                        location: 'share',
                      })
                    }
                  >
                    <LinkedInIconLinkWrapper
                      href={`https://www.linkedin.com/shareArticle?mini=true&url=${createResourceUrl(
                        subdomain
                      )}&title=${encodeURIComponent(title)}`}
                      target="_blank"
                      aria-label="Linkedin share button"
                      rel="noopener noreferrer"
                    >
                      <LinkedInIcon title="LinkedIn" />
                    </LinkedInIconLinkWrapper>
                  </SocialShareIconItem>
                </SocialIcons>
              </SocialIconsWrapper>
            </ResourceFooter>
          </Spacing>
        </ArticleContent>
      </div>
    </>
  );
};
